/**
 * Which screen a table shows is a function of the snapshot and nothing else.
 *
 * The order matters: a decision this viewer owes comes before the phase, and a
 * finished match comes before everything, because those are the states where
 * rendering "the table" would be wrong even though a table exists.
 */
import { useEffect, useState } from 'react'
import type { CardId, Seat, Suit } from '@cards/shared'
import { StatusBand } from './StatusBand'
import { Felt } from './Felt'
import { HandBand } from './HandBand'
import { BidPanel } from './BidPanel'
import { HandSummary } from './HandSummary'
import { MatchEnd } from './MatchEnd'
import { PauseOverlay } from './PauseOverlay'
import { Sheet } from './PromptSheet'
import { MiniCard } from './Card'
import type { TableView } from './view'
import type { TableSend } from '../socket/useTable'
import { useActionLockDelayed } from '../socket/actionLock'
import { useSettings } from '../settings/settings'
import { coachCard, useCoach } from '../coach/useCoach'
import { CoachLine } from '../coach/CoachLine'

export interface TableScreenProps {
  view: TableView
  send: TableSend
  onMenu: () => void
  /** The finished match's own record, from the end screen. */
  onReview?: () => void
  /** The hand just played, from the hand summary. A different screen from
   *  `onReview` because mid-match there is no record to read yet — see
   *  `HandReview`. */
  onReviewHand?: () => void
  onFairness?: () => void
  onLeave?: () => void
  /** Nothing can reach the server — offline, kicked, or a dead session. Every
   *  control reads as busy, because a tap would be discarded. */
  blocked?: boolean
}

export function TableScreen(props: TableScreenProps): JSX.Element {
  const { view, send, onMenu } = props
  const locked = useActionLockDelayed() || props.blocked === true
  const hand = view.hand
  const pending = view.actions?.pending ?? null
  const mine = pending !== null && (view.actions?.controls ?? []).includes(pending.seat)
  const DecisionBody = view.gameUi.DecisionBody
  const closing = useAuctionClose(view)

  const overlays = (
    <PauseOverlay
      view={view}
      onPropose={(seat) => send.proposeReplacement(seat)}
      onVote={(seat, approve) => send.voteReplacement(seat, approve)}
      onReclaim={() => send.reclaimSeat()}
    />
  )

  if (view.match?.status.kind === 'finished') {
    return (
      <>
        <MatchEnd
          view={view}
          locked={locked}
          onRematchChoice={(isIn) => send.rematchChoice(isIn)}
          onReplay={() => props.onReview?.()}
          onLeave={() => props.onLeave?.()}
          onFairness={() => props.onFairness?.()}
        />
        {overlays}
      </>
    )
  }

  if (!hand) {
    return (
      <div className="app">
        <StatusBand view={view} onMenu={onMenu} />
        <section className="felt">
          <div className="result">
            <div className="headline">
              <h1>Shuffling</h1>
              <p>The deal is committed before anyone sees a card.</p>
            </div>
          </div>
        </section>
        <section className="hand-band" />
        {overlays}
      </div>
    )
  }

  // The auction has just closed: the bidding table stays up a moment longer,
  // saying who won it and with what, before the hand moves on. Without it the
  // last pass and the kitty exchange (or the opening lead) were the same
  // frame, and the table changed shape before anyone had seen why.
  if (closing) {
    return (
      <div className="app auction-screen">
        <StatusBand view={view} onMenu={onMenu} />
        <BidPanel view={view} locked onCall={() => {}} closing={closing} />
        <HandBand
          view={view}
          interactive={false}
          hint={closing.contractor === view.mySeat ? <b>You won the bid</b> : `${view.name(closing.contractor)} won the bid`}
        />
        {overlays}
      </div>
    )
  }

  // A decision this viewer owes takes the felt (kitty exchange) or rises as a
  // sheet (Joker nomination) — the game says which.
  if (mine && pending && DecisionBody) {
    const layout = view.gameUi.decisionLayout(pending.kind)
    const body = (
      <DecisionBody
        view={view}
        pending={pending}
        busy={locked}
        onSubmit={(decision) => send.decide(decision)}
      />
    )
    if (layout === 'takeover') {
      return (
        <div className="app">
          <StatusBand view={view} onMenu={onMenu} />
          <section className="felt">{body}</section>
          <section className="hand-band" style={{ height: 28 }}>
            <DecisionHint view={view} fallback={view.gameUi.decisionHint(pending)} />
          </section>
          {overlays}
        </div>
      )
    }
    return (
      <>
        <PlayFrame {...props} locked={locked} />
        <Sheet>{body}</Sheet>
        {overlays}
      </>
    )
  }

  if (hand.phase === 'auction') {
    return (
      <div className="app auction-screen">
        <StatusBand view={view} onMenu={onMenu} />
        <BidPanel view={view} locked={locked} onCall={(call) => send.call(call)} />
        {/* Shaded while somebody else bids and lit on your own call, as
            Trickster's is: its cards are only "legal" for the bidder. */}
        <HandBand
          view={view}
          interactive={false}
          waiting={!view.isMyTurn}
          hint={view.isMyTurn ? <b>Your turn to bid</b> : 'Your hand'}
        />
        {overlays}
      </div>
    )
  }

  // `abandoned` is a hand that ended before it started: everyone passed, so it
  // is thrown in and redealt (500 rules §3.1.6). The server holds it on the
  // table for a moment (`redealHold`) and then deals again on its own, so the
  // auction stays up with every pass on it and the line above the ladder says
  // what is about to happen. It used to share the hand summary — a scorepad
  // with nothing on it and an "I'm ready" button for a hand nobody played —
  // and before that the redeal was instant, and a player's cards simply
  // changed under them with no word of why.
  if (hand.phase === 'abandoned') {
    return (
      <div className="app auction-screen">
        <StatusBand view={view} onMenu={onMenu} />
        <BidPanel view={view} locked onCall={() => {}} redealing />
        <HandBand view={view} interactive={false} waiting hint="Nobody bid" />
        {overlays}
      </div>
    )
  }

  if (hand.phase === 'handComplete') {
    return (
      <div className="app">
        <StatusBand view={view} onMenu={onMenu} />
        <HandSummary
          view={view}
          locked={locked}
          onReady={() => send.ready()}
          onReview={() => props.onReviewHand?.()}
          onFairness={() => props.onFairness?.()}
        />
        {overlays}
      </div>
    )
  }

  return (
    <>
      <PlayFrame {...props} locked={locked} />
      {overlays}
    </>
  )
}

/** How long the closed auction stays on the table. Trickster holds the moment
 *  for ~2.5 s while the kitty goes to the winner, and a touch longer here lets
 *  the contract settle before the kitty moves. The server's bots wait a
 *  little longer than this (`BOT_SETTLE_MS`) so the hold is never cut short by
 *  a bot that has already moved on. */
const AUCTION_CLOSE_MS = 2600

export interface AuctionClose {
  readonly contractor: Seat | null
  readonly label: string
}

/** When each deal's auction was seen to close, by the deal's commitment. Module
 *  state for the same reason the deal's is (Fan.tsx): the screen that shows it
 *  is not the screen that was up when it happened, so no component lives long
 *  enough to remember it. */
const auctionClosedAt = new Map<string, AuctionClose & { at: number; decision: boolean }>()

/** The auction that has just closed, while it is still being shown; null
 *  otherwise. Read from the `auctionEnded` event, so a reconnect into a hand
 *  already under way does not replay it, and dropped the moment the hand moves
 *  on — a card played or a decision made — whatever the clock says. */
function useAuctionClose(view: TableView): AuctionClose | null {
  const key = view.hand?.fairness.commitment ?? null
  let ended: AuctionClose | null = null
  for (const tagged of view.snapshot.events) {
    const e = tagged.event
    if (e.type === 'auctionEnded') ended = { contractor: e.contractor, label: e.contractLabel }
  }
  if (key !== null && ended && !auctionClosedAt.has(key)) {
    auctionClosedAt.clear()
    auctionClosedAt.set(key, { ...ended, at: Date.now(), decision: view.hand?.pending != null })
  }
  const seen = key === null ? undefined : auctionClosedAt.get(key)
  const left = seen ? seen.at + AUCTION_CLOSE_MS - Date.now() : 0
  const [, wake] = useState(0)
  useEffect(() => {
    if (left <= 0) return
    const timer = window.setTimeout(() => wake((n) => n + 1), left)
    return () => window.clearTimeout(timer)
  }, [seen, left > 0])
  if (!seen || left <= 0) return null
  const hand = view.hand
  // A decision owed at the close (the kitty) that has since been made means the
  // hand has moved on even though no card is down yet.
  const untouched =
    hand !== null &&
    (hand.phase === 'decision' || hand.phase === 'play') &&
    hand.tricks.length === 1 &&
    hand.tricks[0].plays.length === 0 &&
    (!seen.decision || hand.pending !== null)
  return untouched ? seen : null
}

/** Duration of the sweep itself; the hold before it is the server's, expressed
 *  as `trickClearAt`, so every client sweeps at the same moment. */
const SWEEP_MS = 520

function useSweep(trickClearAt: number | null): boolean {
  const [gather, setGather] = useState(false)
  useEffect(() => {
    if (trickClearAt === null) {
      setGather(false)
      return
    }
    const delay = trickClearAt - SWEEP_MS - Date.now()
    if (delay <= 0) {
      setGather(true)
      return
    }
    setGather(false)
    const timer = window.setTimeout(() => setGather(true), delay)
    return () => window.clearTimeout(timer)
  }, [trickClearAt])
  return gather
}

function PlayFrame({
  view,
  send,
  onMenu,
  locked,
  blocked,
}: TableScreenProps & { locked: boolean }): JSX.Element {
  const exposed = exposedHands(view)
  const gather = useSweep(view.hand?.trickClearAt ?? null)
  const play = (card: CardId, nominate?: Suit) => {
    const seat = view.actions?.toAct
    if (seat === null || seat === undefined) return
    send.play(seat, card, nominate)
  }
  return (
    <div className={exposed.length > 0 ? 'app has-exposed' : 'app'}>
      <StatusBand view={view} onMenu={onMenu} />
      {exposed.map((e) => (
        <ExposedHand
          key={e.seat}
          view={view}
          seat={e.seat}
          cards={e.cards}
          onPlay={view.actions?.toAct === e.seat && view.isMyTurn && blocked !== true && !locked ? play : undefined}
        />
      ))}
      <Felt view={view} gather={gather} />
      {/* While nothing can reach the server the hand is reference, not a
          control — the same flat treatment the auction screen uses. A lifted
          card invites a tap that would be discarded. */}
      <HandBand
        view={view}
        interactive={view.isMyTurn && blocked !== true}
        waiting={!view.isMyTurn}
        locked={locked}
        onPlay={play}
      />
    </div>
  )
}

/** The line under a takeover decision (the kitty): the coach's suggestion
 *  when coach mode is on, the game's own hint otherwise. */
function DecisionHint({ view, fallback }: { view: TableView; fallback: React.ReactNode }): JSX.Element {
  const coach = useCoach(view)
  return (
    <div className="hint" style={{ justifyContent: 'center' }}>
      {coach ? <CoachLine view={view} state={coach} /> : <div className="hint-text muted">{fallback}</div>}
    </div>
  )
}

/** A holding the server has chosen to expose — the Open Misère contractor after
 *  trick 1, Bridge's dummy. Generic: the client shows what it is given. */
function exposedHands(view: TableView): { seat: number; cards: readonly CardId[] }[] {
  const out: { seat: number; cards: readonly CardId[] }[] = []
  for (const seat of view.seats) {
    if (seat.isMe) continue
    if (seat.holding?.kind === 'cards' && seat.holding.cards.length > 0) {
      out.push({ seat: seat.seat, cards: seat.holding.cards })
    }
  }
  return out
}

/**
 * An open hand above the felt. When it is this viewer's to play from — the
 * declarer on dummy's turn — its legal cards are the controls, exactly as the
 * hand band's are; otherwise it is reference.
 *
 * Thirteen cards do not fit side by side on a phone, so the row overlaps the
 * way the hand fan does, far enough to leave every index corner showing.
 */
function ExposedHand({
  view,
  seat,
  cards,
  onPlay,
}: {
  view: TableView
  seat: number
  cards: readonly CardId[]
  onPlay?: (card: CardId) => void
}): JSX.Element {
  const [settings] = useSettings()
  const ghost = coachCard(useCoach(view))
  const sorted = view.gameUi.sortHand?.(view, cards) ?? cards
  const step = Math.min(44, cards.length > 1 ? Math.floor((358 - 38) / (cards.length - 1)) : 44)
  return (
    <div className={onPlay ? 'exposed yours' : 'exposed'}>
      <div className="exposed-head">
        <b>{onPlay ? `Play from ${view.name(seat)}'s hand` : `${view.name(seat)}'s hand, open`}</b>
        <span className="muted">{cards.length} cards left</span>
      </div>
      <div className="exposed-cards" style={{ ['--step' as string]: `${step}px` }}>
        {sorted.map((card) => {
          if (!onPlay) return <MiniCard key={card} card={card} deck={settings.deck} />
          const legal = view.legalPlays.has(card)
          return (
            <button
              key={card}
              type="button"
              className={`mini-play ${legal ? 'playable' : 'unplayable'}${legal && ghost?.card === card ? ' coach' : ''}`}
              disabled={!legal}
              onClick={() => onPlay(card)}
              aria-label={`Play ${card} from ${view.name(seat)}'s hand`}
            >
              <MiniCard card={card} deck={settings.deck} />
            </button>
          )
        })}
      </div>
    </div>
  )
}
